import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { ValidatorForm } from 'react-form-validator-core';
import TextValidator from '../../components/partial/TextValidator';
import swal from 'sweetalert';
import { getCustomer, createCustomer, getCustomerbyID, updateCustomer } from '../../components/actions/campaign';
import { getCustomerSchema } from '../../components/actions/schema';
import Loader from '../../components/utils/Loader';

function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [schema, setSchema] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [tierFilter, setTierFilter] = useState('All Tiers');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [successAction, setSuccessAction] = useState(''); // 'add' or 'update'
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formDisable, setFormDisable] = useState(false);
  const [requestData, setRequestData] = useState({});
  const navigate = useNavigate();
  const code = sessionStorage.getItem('code');

  // Fetch customer data and schema
  useEffect(() => {
    setLoading(true);
    Promise.all([getCustomerSchema(code), getCustomer(code)])
      .then(([schemaRes, customerRes]) => {
        const schemaData = schemaRes?.data || [];
        const customerData = (customerRes?.data || []).sort((a, b) => {
          const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bTime - aTime;
        });
        setSchema(schemaData);
        setCustomers(customerData);
        const jsonDataInit = schemaData.reduce((acc, item) => {
          const defaultValue = item.fieldtype === 'integer' || item.fieldtype === 'float' ? 0 : '';
          acc[item.jsonfield] = defaultValue;
          return acc;
        }, {});
        setRequestData(jsonDataInit);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        swal({
          position: 'center',
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch schema or customers',
          showConfirmButton: true,
        });
      });
  }, [code]);

  // Helper function to get nested values from schema
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => {
      return current ? current[key] : '';
    }, obj);
  };

  // Filter customers based on search, status, and tier
  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch = schema
      .filter((field) => field.isdisplay === '1')
      .some((field) => {
        const value = getNestedValue(customer, field.jsonfield)?.toString().toLowerCase();
        return value?.includes(searchTerm.toLowerCase());
      }) || (customer.customer_id?.toString().toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'All Status' || customer.status === statusFilter;
    const matchesTier = tierFilter === 'All Tiers' || customer.tier === tierFilter;

    return matchesSearch && matchesStatus && matchesTier;
  });

  const totalPages = Math.ceil(filteredCustomers.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedCustomers = filteredCustomers.slice(startIndex, startIndex + entriesPerPage);

  // Calculate statistics
  const totalCustomers = customers.length;
  const vipCustomers = customers.filter((c) => c.status === 'VIP').length;
  const activeUsers = customers.filter((c) => c.status === 'Active').length;
  const avgPoints = Math.round(
    customers.reduce((sum, c) => sum + (c.totalPoints || 0), 0) / (customers.length || 1)
  );

  // Handle input changes in the form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const field = schema.find((item) => item.jsonfield === name);
    let formattedValue = value;

    if (field && (field.fieldtype === 'integer' || field.fieldtype === 'float')) {
      formattedValue = value === '' ? 0 : field.fieldtype === 'integer' ? parseInt(value) : parseFloat(value);
      if (isNaN(formattedValue)) {
        formattedValue = 0;
      }
    } else if (!field) {
      console.warn(`Field ${name} not found in schema`);
    }

    setRequestData({ ...requestData, [name]: formattedValue });
  };

  // Reset form data
  const resetForm = () => {
    const jsonDataInit = schema.reduce((acc, item) => {
      const defaultValue = item.fieldtype === 'integer' || item.fieldtype === 'float' ? 0 : '';
      acc[item.jsonfield] = defaultValue;
      return acc;
    }, {});
    setRequestData(jsonDataInit);
  };

  // Handle add customer form submission
  const handleAddCustomer = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormDisable(true);

    const finalData = schema.reduce((acc, item) => {
      const value = requestData[item.jsonfield] !== undefined
        ? requestData[item.jsonfield]
        : (item.fieldtype === 'integer' || item.fieldtype === 'float' ? 0 : '');
      acc[item.jsonfield] = value;
      return acc;
    }, {});

    try {
      const response = await createCustomer(code, finalData);
      if (response.message === 'success') {
        const customerRes = await getCustomer(code);
        const customerData = (customerRes?.data || []).sort((a, b) => {
          const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bTime - aTime;
        });
        setCustomers(customerData);
        setCurrentPage(1);
        setFormLoading(false);
        setFormDisable(false);
        resetForm();
        setShowAddModal(false);
        setNewCustomerName(finalData.name || 'Customer');
        setSuccessAction('add');
        setShowSuccessModal(true);
      }
    } catch (error) {
      setFormLoading(false);
      setFormDisable(false);
      swal({
        position: 'center',
        icon: 'error',
        title: 'Error',
        text: error.message || 'An error occurred',
        showConfirmButton: true,
      });
    }
  };

  // Handle edit customer form submission
  const handleEditCustomer = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormDisable(true);

    const finalData = schema.reduce((acc, item) => {
      const value = requestData[item.jsonfield] !== undefined
        ? requestData[item.jsonfield]
        : (item.fieldtype === 'integer' || item.fieldtype === 'float' ? 0 : '');
      acc[item.jsonfield] = value;
      return acc;
    }, {});

    try {
      const payload = {
        ...finalData,
        compId: code || '',
        customerID: selectedCustomer?.customer_id,
      };
      const response = await updateCustomer(code, payload);
      if (response.message === 'success') {
        const customerRes = await getCustomer(code);
        const customerData = (customerRes?.data || []).sort((a, b) => {
          const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bTime - aTime;
        });
        setCustomers(customerData);
        setCurrentPage(1);
        setFormLoading(false);
        setFormDisable(false);
        resetForm();
        setShowEditModal(false);
        setNewCustomerName(finalData.name || 'Customer');
        setSuccessAction('update');
        setShowSuccessModal(true);
      }
    } catch (error) {
      setFormLoading(false);
      setFormDisable(false);
      swal({
        position: 'center',
        icon: 'error',
        title: 'Error',
        text: error.message || 'An error occurred',
        showConfirmButton: true,
      });
    }
  };

  // Handle edit button click
  const handleEdit = async (id) => {
    setLoading(true);
    try {
      const customer = customers.find((c) => c.customer_id === id);
      if (customer) {
        const filteredCustomerData = Object.fromEntries(
          Object.entries(customer).filter(
            ([key]) => !['compid', 'customer_id', 'createdAt', 'updatedAt'].includes(key)
          )
        );
        const schemaFields = schema.reduce((acc, item) => {
          const value = filteredCustomerData[item.jsonfield];
          acc[item.jsonfield] = (item.fieldtype === 'integer' || item.fieldtype === 'float') && value !== undefined && !isNaN(value)
            ? Number(value)
            : (value || '');
          return acc;
        }, {});
        setRequestData(schemaFields);
        setSelectedCustomer(customer);
        setShowEditModal(true);
      } else {
        const res = await getCustomerbyID(id, code);
        if (res.data && res.data.length > 0) {
          const filteredCustomerData = Object.fromEntries(
            Object.entries(res.data[0]).filter(
              ([key]) => !['compid', 'customerID', 'createdAt', 'updatedAt'].includes(key)
            )
          );
          const schemaFields = schema.reduce((acc, item) => {
            const value = filteredCustomerData[item.jsonfield];
            acc[item.jsonfield] = (item.fieldtype === 'integer' || item.fieldtype === 'float') && value !== undefined && !isNaN(value)
              ? Number(value)
              : (value || '');
            return acc;
          }, {});
          setRequestData(schemaFields);
          setSelectedCustomer({ customer_id: id, ...res.data[0] });
          setShowEditModal(true);
        } else {
          throw new Error('Customer not found');
        }
      }
    } catch (error) {
      swal({
        position: 'center',
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to fetch customer data',
        showConfirmButton: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleView = (id) => {
    navigate('/customers', { state: { customerID: id } });
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Platinum':
        return 'bg-gray-800 text-white';
      case 'Gold':
        return 'bg-yellow-500 text-white';
      case 'Silver':
        return 'bg-gray-400 text-white';
      case 'Bronze':
        return 'bg-orange-500 text-white';
      default:
        return 'bg-blue-500 text-white';
    }
  };

  // Dynamic columns based on schema
  const displayedFields = schema.filter((field) => field.isdisplay === '1');
  const columns = [
    {
      name: 'Customer ID',
      selector: (row) => row.customer_id || '-',
    },
    ...displayedFields.map((field) => ({
      name: field.displayfield,
      selector: (row) => {
        const value = getNestedValue(row, field.jsonfield) || '-';
        if (field.jsonfield === 'tier') {
          return (
            <span className={`px-2 py-1 rounded-full text-xs ${getTierColor(value)}`}>
              {value}
            </span>
          );
        }
        return value;
      },
    })),
    {
      name: 'Action',
      selector: (row) => (
        <div className="flex items-center gap-2">
          <button
            className="text-blue-600 hover:text-blue-800 cursor-pointer"
            onClick={() => handleEdit(row.customer_id)}
          >
            <i className="ri-edit-line text-lg"></i>
          </button>
          <Link
            to={`/customers/${row.customer_id}`}
            className="text-blue-600 hover:text-blue-800 cursor-pointer"
            onClick={() => handleView(row.customer_id)}
          >
            <i className="ri-eye-line text-lg"></i>
          </Link>
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout>
      {(loading || formLoading) && <Loader />}
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Customer Management</h1>
            <p className="text-gray-600 mt-1">Manage and track your loyal customers</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-600 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap"
          >
            <i className="ri-add-line text-lg"></i>
            Add New Customer
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="ri-user-line text-blue-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Customers</p>
                <p className="text-2xl font-bold text-gray-900">{totalCustomers}</p>
                <p className="text-green-600 text-xs font-semibold">↗ +2.5% from last month</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <i className="ri-vip-crown-line text-purple-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">VIP Customers</p>
                <p className="text-2xl font-bold text-gray-900">{vipCustomers}</p>
                <p className="text-green-600 text-xs font-semibold">↗ +8.2% growth</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-user-heart-line text-green-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Active Users</p>
                <p className="text-2xl font-bold text-gray-900">{activeUsers}</p>
                <p className="text-green-600 text-xs font-semibold">↗ +5.3% active</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <i className="ri-coin-line text-orange-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Avg. Points</p>
                <p className="text-2xl font-bold text-gray-900">{avgPoints.toLocaleString()}</p>
                <p className="text-green-600 text-xs font-semibold">↗ +2% vs earned</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 items-center w-full lg:w-auto">
              <div className="relative w-full sm:w-80">
                <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search customers by ID, name, email, or other fields..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-sm bg-gray-50"
                />
              </div>
            </div>

            <div className="flex gap-3 items-center">
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                >
                  <option value="All Status">All Status</option>
                  <option value="VIP">VIP</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
              </div>

              <div className="relative">
                <select
                  value={tierFilter}
                  onChange={(e) => setTierFilter(e.target.value)}
                  className="appearance-none bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                >
                  <option value="All Tiers">All Tiers</option>
                  <option value="Platinum">Platinum</option>
                  <option value="Gold">Gold</option>
                  <option value="Silver">Silver</option>
                  <option value="Bronze">Bronze</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show</span>
                <select
                  value={entriesPerPage}
                  onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                  className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer pr-8"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-600">entries</span>
              </div>
            </div>

            <div className="text-sm text-gray-600">
              Showing {filteredCustomers.length} results
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {columns.map((col, index) => (
                    <th
                      key={index}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {col.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedCustomers.map((customer) => (
                  <tr key={customer.customer_id} className="hover:bg-gray-50">
                    {columns.map((col, index) => (
                      <td key={index} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {col.selector(customer)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1} to{' '}
              {Math.min(startIndex + entriesPerPage, filteredCustomers.length)} of{' '}
              {filteredCustomers.length} entries
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                Previous
              </button>

              {[...Array(Math.min(5, totalPages))].map((_, index) => {
                const pageNum = index + 1;
                if (pageNum > totalPages) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 border rounded text-sm cursor-pointer ${
                      currentPage === pageNum
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Add Customer Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-3xl w-full mx-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Customer</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <ValidatorForm className="space-y-4" onSubmit={handleAddCustomer}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {schema.map((item) => (
                    <div key={item.jsonfield}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {item.jsonfield?.charAt(0).toUpperCase() + item.jsonfield.slice(1)}
                      </label>
                      <TextValidator
                        type={item.fieldtype === 'integer' || item.fieldtype === 'float' ? 'number' : 'text'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        id={item.jsonfield}
                        name={item.jsonfield}
                        value={requestData[item.jsonfield] !== undefined ? requestData[item.jsonfield] : ''}
                        onChange={handleInputChange}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors cursor-pointer"
                    disabled={formDisable}
                  >
                    {formLoading ? 'Submitting...' : 'Add Customer'}
                  </button>
                </div>
              </ValidatorForm>
            </div>
          </div>
        )}

        {/* Edit Customer Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-3xl w-full mx-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Customer</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <ValidatorForm className="space-y-4" onSubmit={handleEditCustomer}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {schema.map((item) => (
                    <div key={item.jsonfield}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {item.jsonfield?.charAt(0).toUpperCase() + item.jsonfield.slice(1)}
                      </label>
                      <TextValidator
                        type={item.fieldtype === 'integer' || item.fieldtype === 'float' ? 'number' : 'text'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        id={item.jsonfield}
                        name={item.jsonfield}
                        value={requestData[item.jsonfield] !== undefined ? requestData[item.jsonfield] : ''}
                        onChange={handleInputChange}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors cursor-pointer"
                    disabled={formDisable}
                  >
                    {formLoading ? 'Submitting...' : 'Update Customer'}
                  </button>
                </div>
              </ValidatorForm>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-add-line text-3xl text-green-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Customer {successAction === 'add' ? 'Added' : 'Updated'} Successfully
                </h2>
                <p className="text-gray-600 mb-6">
                  Customer "{newCustomerName}" has been {successAction === 'add' ? 'added to' : 'updated in'} your loyalty program.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowSuccessModal(false);
                      setSuccessAction('');
                      if (successAction === 'add') {
                        setShowAddModal(true);
                      } else {
                        setShowEditModal(true);
                      }
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    {successAction === 'add' ? 'Add Another' : 'Update Another'}
                  </button>
                  <button
                    onClick={() => {
                      setShowSuccessModal(false);
                      setSuccessAction('');
                      navigate('/customers');
                    }}
                    className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors cursor-pointer"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default CustomersPage;